// Pantalla de empaquetado — la pantalla operativa principal.
//
// Flujo:
// 1. Carga el pedido al backend.
// 2. Pinta líneas con su estado (pendiente/parcial/done).
// 3. "Escanear" abre el overlay; al decodificar:
//    - matchea barcode contra una línea del pedido
//    - si NO coincide → fullscreen alert "fuera del pedido"
//    - si SÍ → abre modal de lote (a menos que ya esté memorizado para esta caja)
//    - actualiza el store y repinta
// 4. "Caja lista" → POST /cajas/crear → modal SSCC o PDF.
// 5. "Cerrar pedido" → POST /pedidos/{id}/cerrar (con permitir_incompleto si falta).

import { ApiError, apiDel, apiGet, apiPatch, apiPost } from '../api/client.js';
import {
  openAlert,
  openCajaOkModal,
  openCajaPdfModal,
  openIncompletoModal,
  openLoteModal,
  openPaletOkModal,
  openPaletPdfModal,
  openPedidoCerradoLightbox,
} from '../components/modal.js';
import { icon } from '../components/icons.js';
import { fontScaleButtonHTML } from '../components/font-scale.js';
import { openAccionesLineaModal } from '../components/modal-acciones-linea.js';
import { openAnadirProductoModal } from '../components/modal-anadir-producto.js';
import { openCambiarLoteModal } from '../components/modal-cambiar-lote.js';
import { openConfirmarCajaModal } from '../components/modal-confirmar-caja.js';
import { openEditarLineaModal } from '../components/modal-editar-linea.js';
import { openEstructuraModal } from '../components/modal-estructura.js';
import { openOverridesModal } from '../components/modal-overrides.js';
import { openPlanPackingModal, resumenPlanCorto } from '../components/modal-plan-packing.js';
import { openScanner } from '../components/scanner.js';
import {
  addUnidades,
  cajasEnPalet,
  changeLote,
  clearPaletActivo,
  closeCaja,
  empaquetadasDe,
  getLoteMemorizado,
  getState,
  loadPedido,
  pedidoCompleto,
  pesoCajaActual,
  reset as resetState,
  setPackingPlan,
  setPaletActivo,
  subscribe,
  updatePedido,
} from '../state/pedido.js';
import { navigate } from '../router.js';

const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g,
  c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));

function lineaHTML(linea, empaquetado) {
  const pedida = linea.cantidad_pedida;
  let cls = '';
  if (empaquetado >= pedida) cls = ' --done';
  else if (empaquetado > 0)  cls = ' --partial';
  // Hint del lote memorizado (si lo hay), también pista de que la línea
  // es tappable para cambiarlo.
  const loteMemo = getLoteMemorizado(linea.producto_id);
  const loteHint = loteMemo
    ? `<div style="font-size:var(--fs-xs);color:var(--c-fg-muted);display:flex;align-items:center;gap:0.25rem;margin-top:2px;">
        ${icon('tag', { size: 12 })} <span>lote: <strong>${escapeHtml(loteMemo)}</strong></span>
        <span style="margin-left:auto;color:var(--c-accent);">cambiar →</span>
      </div>`
    : '';
  return `
    <div class="line${cls}" data-producto-id="${linea.producto_id}">
      <div class="body">
        <div class="ref">${escapeHtml(linea.barcode || `#${linea.producto_id}`)}</div>
        <div class="desc">${escapeHtml(linea.descripcion)}</div>
        ${loteHint}
      </div>
      <div class="counter">${empaquetado}<span class="total">/${pedida}</span></div>
    </div>`;
}

export function empaquetadoView(params) {
  const pedidoId = parseInt(params.id, 10);
  let unsubscribe = null;
  let wakeLock = null;
  let scannerCloser = null;
  let mounted = false;

  // Abre el scanner si no hay uno ya abierto. Idempotente — la usa tanto
  // el botón "Escanear" como el re-arranque automático tras el modal de
  // lote (para que cambiar de producto en modo continuo no obligue al
  // operario a tocar "Escanear" otra vez).
  async function _startScannerSession() {
    if (scannerCloser || !mounted) return;
    scannerCloser = await openScanner({
      onDecode: handleScan,
      onClose: () => { scannerCloser = null; },
    });
  }

  async function _requestWakeLock() {
    if (!('wakeLock' in navigator)) return;
    try { wakeLock = await navigator.wakeLock.request('screen'); }
    catch (e) { console.info('Wake lock no disponible:', e.message); }
  }
  function _releaseWakeLock() {
    try { wakeLock?.release(); } catch {}
    wakeLock = null;
  }

  // Trae el plan de packing del backend y lo mete al state. Best-effort:
  // si el endpoint falla (sin dims, Dolibarr caído) lo logueamos y dejamos
  // el banner oculto — no es bloqueante para empaquetar.
  async function _refrescarPackingPlan(pid) {
    try {
      const plan = await apiGet(`/pedidos/${pid}/packing`);
      setPackingPlan(plan);
    } catch (e) {
      if (e?.status === 401) return;
      console.warn('Packing plan no disponible:', e?.message || e);
      setPackingPlan(null);
    }
  }

  function rerender(root) {
    if (!mounted) return;
    const state = getState();
    if (!state.pedido) return;

    const lineasEl = root.querySelector('#lineas');
    if (lineasEl) {
      lineasEl.innerHTML = state.pedido.lineas
        .map(l => lineaHTML(l, empaquetadasDe(l.producto_id)))
        .join('');
    }

    const numeroCaja = state.ssccs.length + 1;
    const nItemsEnCaja = state.cajaActual.lineas.length;

    // Botón Caja con badge contador. Vacía → texto "Caja lista" disabled.
    // Con N productos → "Caja (N)" habilitado, abre preview al pulsar.
    const btnCaja = root.querySelector('#btn-caja');
    if (btnCaja) {
      btnCaja.disabled = nItemsEnCaja === 0;
      btnCaja.innerHTML = nItemsEnCaja === 0
        ? `${icon('package', { size: 22 })} Caja lista`
        : `${icon('package', { size: 22 })} Caja (${nItemsEnCaja})`;
    }

    // Badge "Modificado" en topbar si alguna línea tiene override.
    const badgeMod = root.querySelector('#badge-modificado');
    if (badgeMod) {
      const hayOverrides = state.pedido.lineas.some(l => l.modificada);
      badgeMod.hidden = !hayOverrides;
    }

    // Indicador de palet activo (Fase 10). Estilo "card" que respira para que
    // sea obvio que las cajas siguientes irán al palet.
    const palIndic = root.querySelector('#palet-indicador');
    if (palIndic) {
      if (state.paletActivo) {
        const last8 = state.paletActivo.sscc.slice(-8);
        const n = cajasEnPalet(state.paletActivo.sscc);
        const cuenta = n === 0 ? 'vacío' : `${n} ${n === 1 ? 'caja' : 'cajas'}`;
        palIndic.hidden = false;
        palIndic.innerHTML = `
          <div style="
            background: var(--c-surface);
            border: 1px solid var(--c-border);
            border-left: 5px solid var(--c-accent);
            border-radius: var(--radius);
            padding: 0.55rem 0.75rem;
            display: flex; align-items: center; gap: 0.5rem;
            font-size: var(--fs-sm);
            box-shadow: var(--shadow-sm);
          ">
            <span style="color:var(--c-accent);display:inline-flex;">${icon('layers', { size: 22 })}</span>
            <span style="flex:1;">
              <strong>Palet #${state.paletActivo.numero}</strong> activo · <strong>${cuenta}</strong>
              <span style="color: var(--c-fg-muted); font-family: ui-monospace, monospace; display:block; font-size: var(--fs-xs);">…${last8}</span>
            </span>
            <button class="btn --ghost" id="btn-cerrar-palet" style="min-height: 36px; padding: 0 0.6rem; font-size: var(--fs-sm);display:inline-flex;align-items:center;gap:0.3rem;">
              ${icon('x', { size: 16 })} Cerrar palet
            </button>
          </div>`;
        palIndic.querySelector('#btn-cerrar-palet').addEventListener('click', () => {
          clearPaletActivo();
        });
      } else {
        palIndic.hidden = true;
        palIndic.innerHTML = '';
      }
    }

    const btnCerrar = root.querySelector('#btn-cerrar');
    if (btnCerrar) {
      const completo = pedidoCompleto();
      const tieneCajas = state.ssccs.length > 0;
      btnCerrar.disabled = !tieneCajas;
      btnCerrar.innerHTML = completo
        ? `${icon('check-circle', { size: 22 })} Cerrar pedido`
        : `${icon('flag', { size: 22 })} Cerrar pedido (incompleto)`;
      btnCerrar.classList.toggle('--primary', completo);
    }

    // Banner del plan de packing (Fase 11) — visible si hay plan cargado.
    const planBanner = root.querySelector('#packing-banner');
    if (planBanner) {
      const plan = state.packingPlan;
      if (!plan) {
        planBanner.hidden = true;
      } else {
        const resumen = resumenPlanCorto(plan);
        const hayAdvertencias = plan.items_no_asignados.length > 0 || plan.advertencias.length > 0;
        const sigPct = porcentajeSiguiendoPlan(plan, state);
        if (!resumen && !hayAdvertencias) {
          planBanner.hidden = true;
        } else {
          planBanner.hidden = false;
          // Estilo neutro por defecto; ámbar suave si hay warnings.
          if (hayAdvertencias) {
            planBanner.style.background = '#fef3c7';
            planBanner.style.color = '#78350f';
            planBanner.style.borderColor = '#fcd34d';
          } else {
            planBanner.style.background = 'var(--c-surface)';
            planBanner.style.color = 'var(--c-fg)';
            planBanner.style.borderColor = 'var(--c-border)';
          }
          const sigTxt = sigPct !== null
            ? ` <span style="color:var(--c-fg-muted);">· siguiendo ${sigPct}%</span>`
            : '';
          const warningPrefix = hayAdvertencias
            ? `<span style="color:#b45309;">${icon('alert-triangle', { size: 18 })}</span>`
            : `<span style="color:var(--c-fg-muted);">${icon('package-search', { size: 18 })}</span>`;
          planBanner.innerHTML = `
            <span style="display:inline-flex;">${warningPrefix}</span>
            <span style="flex:1;">
              <strong>Plan sugerido${resumen ? `: ${escapeHtml(resumen)}` : ''}</strong>${sigTxt}
            </span>
            <span style="font-size:var(--fs-xs);color:var(--c-fg-muted);">Ver →</span>`;
        }
      }
    }
  }

  /** Calcula best-effort qué porcentaje de las cajas propuestas se ha
   *  cumplido. Algoritmo simple: por cada caja real (en state.ssccs +
   *  cajaActual), buscar la propuesta con mejor solapamiento de items y
   *  marcarla como cumplida. Devuelve null si no hay propuestas. */
  function porcentajeSiguiendoPlan(plan, state) {
    if (!plan?.propuestas || plan.propuestas.length === 0) return null;
    const totalCajasReales = state.ssccs.length + (state.cajaActual.lineas.length > 0 ? 1 : 0);
    if (totalCajasReales === 0) return 0;
    // Best-effort: contamos cuántas propuestas tienen al menos un item
    // representado en lo ya empacado del pedido. No perfecto pero útil.
    const empacadosPorProducto = state.empaquetado || {};
    let cumplidas = 0;
    for (const prop of plan.propuestas) {
      const todoCubierto = prop.items.every(it => (empacadosPorProducto[it.producto_id] || 0) > 0);
      if (todoCubierto) cumplidas++;
    }
    return Math.round(100 * cumplidas / plan.propuestas.length);
  }

  // OJO: NO marcar como `async`. El scanner espera un valor sincrónico
  // ('consume' | 'continue'); si esta función fuera async devolvería un
  // Promise y `action === 'consume'` daría false, dejando la cámara abierta
  // mientras el modal de lote intenta dispararse encima (síntoma reportado:
  // "no cerró la cámara y salió el teclado del móvil"). Las acciones
  // diferidas que sí necesitan await viven dentro de `setTimeout(async …)`.
  //
  // **Modo continuo (2026-05-17)**: cuando el lote del producto ya está
  // memorizado en esta caja, registramos +1 y devolvemos 'continue' para
  // que la cámara siga abierta. Así escanear 24 cajas iguales no requiere
  // tocar el botón 24 veces. Pintamos feedback con `scannerCloser.setStatus`.
  function handleScan(value, format) {
    const state = getState();
    if (!state.pedido) return 'continue';
    const linea = state.pedido.lineas.find(l => l.barcode && l.barcode === value);

    if (!linea) {
      setTimeout(() => openAlert({
        tipo: 'error',
        titulo: 'Producto fuera del pedido',
        detalle: `EAN ${value} (${format}) no pertenece a este pedido.`,
      }), 50);
      return 'consume';
    }

    const yaEmpacado = empaquetadasDe(linea.producto_id);
    if (yaEmpacado >= linea.cantidad_pedida) {
      setTimeout(() => openAlert({
        tipo: 'warn',
        titulo: 'Cantidad ya completa',
        detalle: `${linea.descripcion}: ${yaEmpacado}/${linea.cantidad_pedida} ya empaquetadas.`,
      }), 50);
      return 'consume';
    }

    // --- Camino feliz continuo: lote ya conocido en esta caja ---
    const memoLote = state.lotes[linea.producto_id];
    if (memoLote) {
      addUnidades(linea, 1, memoLote);
      const nuevoTotal = empaquetadasDe(linea.producto_id);

      // Si esta lectura acaba de COMPLETAR la línea, salimos del modo
      // continuo y avisamos con un alert "ok" — el operario pasa al
      // siguiente producto (o pulsa Caja lista).
      if (nuevoTotal >= linea.cantidad_pedida) {
        setTimeout(() => openAlert({
          tipo: 'ok',
          titulo: 'Línea completada',
          detalle: `${linea.descripcion}: ${nuevoTotal} / ${linea.cantidad_pedida}. Sigue con el siguiente producto.`,
        }), 50);
        return 'consume';
      }

      // Sigue escaneando — pintar progreso en la cámara.
      try {
        scannerCloser?.setStatus?.(`${linea.descripcion} · ${nuevoTotal}/${linea.cantidad_pedida}`);
      } catch (e) { /* ignore */ }
      return 'continue';
    }

    // --- Primera vez de este producto en esta caja: pedir lote ---
    // Cerramos el scanner (el input del modal de lote compite con el foco)
    // y, tras confirmar, lo reabrimos automáticamente para que el flujo
    // continuo no se rompa al cambiar de producto.
    setTimeout(async () => {
      const result = await openLoteModal({ producto: linea, loteSugerido: null });
      if (!result) return;  // canceló — quedó sin registrar, no reabrimos
      addUnidades(linea, 1, result);
      // Si esta lectura ya completó la línea (caja de 1 unidad pedida), no
      // reabrimos — mejor el alert "completada" que aparece en la siguiente.
      const nuevoTotal = empaquetadasDe(linea.producto_id);
      if (nuevoTotal >= linea.cantidad_pedida) {
        await openAlert({
          tipo: 'ok',
          titulo: 'Línea completada',
          detalle: `${linea.descripcion}: ${nuevoTotal} / ${linea.cantidad_pedida}. Sigue con el siguiente producto.`,
        });
        return;
      }
      // Reabrir scanner para que el operario siga escaneando.
      await _startScannerSession();
      try { scannerCloser?.setStatus?.(`${linea.descripcion} · ${nuevoTotal}/${linea.cantidad_pedida}`); } catch {}
    }, 50);
    return 'consume';
  }

  return {
    async mount(root) {
      mounted = true;

      root.innerHTML = `
        <div class="app">
          <header class="topbar">
            <h1 id="ped-title">…<span class="subtitle" id="ped-cliente">Cargando…</span></h1>
            <button id="badge-modificado" hidden type="button"
              title="Ver cambios del pedido"
              style="
                background: var(--c-partial); color: #fff;
                font-size: var(--fs-xs); font-weight: 600;
                padding: 0.3rem 0.6rem; border-radius: 999px;
                display: inline-flex; align-items: center; gap: 0.25rem;
                margin-right: 0.25rem;
                border: 0; cursor: pointer;
              ">${icon('pencil', { size: 14 })} Modificado</button>
            ${fontScaleButtonHTML()}
            <button class="iconbtn" id="btn-estructura" title="Estructura del pedido" aria-label="Estructura">${icon('folder-tree', { size: 22 })}</button>
            <button class="iconbtn" id="btn-back" title="Volver" aria-label="Volver">${icon('arrow-left', { size: 22 })}</button>
          </header>
          <main class="content" style="padding-bottom: 200px;">
            <!-- Banner sync — visible solo si pedido_cerrado && sync_pendiente.
                 Se muestra arriba del scroll para máxima visibilidad. -->
            <div id="sync-banner" hidden class="sync-banner"></div>

            <!-- Banner del plan de packing (Fase 11). Sticky para que el
                 operario lo vea siempre. Neutro por defecto, ámbar si hay
                 advertencias. Tap → modal con detalle por caja. -->
            <button id="packing-banner" hidden type="button" style="
              all: unset;
              cursor: pointer;
              background: var(--c-surface);
              color: var(--c-fg);
              padding: 0.6rem 0.9rem;
              border-radius: var(--radius);
              display: flex;
              align-items: center;
              gap: 0.6rem;
              font-size: var(--fs-sm);
              margin-bottom: 0.5rem;
              border: 1px solid var(--c-border);
              box-shadow: var(--shadow-sm);
            "></button>

            <h3 class="section-title">Productos del pedido</h3>
            <div id="lineas" style="display:flex;flex-direction:column;gap:0.5rem;">
              <p style="color:var(--c-fg-muted);">Cargando…</p>
            </div>

            <!-- Botón añadir producto extra al pedido (override 'añadida') -->
            <button class="btn --ghost --block" id="btn-anadir-producto" type="button"
                    style="margin-top:0.5rem;display:inline-flex;align-items:center;justify-content:center;gap:0.4rem;">
              + Añadir producto al pedido
            </button>

            <!-- Indicador de palet activo (Fase 10). Oculto cuando no hay palet.
                 Vive al final del scroll natural (no se queda fijo) para no robar
                 espacio al footer de acciones. -->
            <div id="palet-indicador" hidden style="margin-top:0.5rem;"></div>
          </main>

          <!-- Footer fijo con las 4 acciones principales — el operario no
               necesita buscarlas tras el scroll. La preview del contenido
               de la caja sale como sheet al pulsar el botón "Caja". -->
          <footer style="
              position: fixed; left: 0; right: 0; bottom: 0; z-index: 20;
              max-width: 480px; margin: 0 auto;
              background: linear-gradient(180deg, rgba(243,244,246,0) 0%, var(--c-bg) 25%, var(--c-bg) 100%);
              padding: 0.75rem var(--pad) calc(0.75rem + env(safe-area-inset-bottom, 0));
              display: flex; flex-direction: column; gap: 0.5rem;
              border-top: 1px solid var(--c-border);
            ">
            <div style="display:flex;gap:0.5rem;">
              <button class="btn --scan --xl" id="btn-scan" type="button" style="flex:2;">
                ${icon('camera', { size: 26 })} Escanear
              </button>
              <button class="btn --primary --xl" id="btn-caja" type="button" style="flex:1;" disabled>
                ${icon('package', { size: 26 })} Caja lista
              </button>
            </div>
            <div style="display:flex;gap:0.5rem;">
              <button class="btn --ghost" id="btn-nuevo-palet" type="button" style="flex:1;min-height:48px;">
                ${icon('layers', { size: 20 })} Nuevo palet
              </button>
              <button class="btn --ghost" id="btn-cerrar" type="button" style="flex:1;min-height:48px;" disabled>
                ${icon('flag', { size: 20 })} Cerrar pedido
              </button>
            </div>
          </footer>
        </div>`;

      root.querySelector('#btn-back').addEventListener('click', () => navigate('#/pedidos'));

      // Tap en una línea de producto → sheet con 3 acciones:
      // cambiar lote (memorizar para próximos scans), cambiar cantidad pedida
      // (override modificada en backend) o quitar la línea (override eliminada).
      // El sheet enruta a los modales específicos.
      root.querySelector('#lineas').addEventListener('click', (e) => {
        const row = e.target.closest('.line[data-producto-id]');
        if (!row) return;
        handleTapLinea(row).catch(err => console.error('tap línea falló', err));
      });

      async function handleTapLinea(row) {
        const productoId = parseInt(row.dataset.productoId, 10);
        const state = getState();
        if (!state.pedido) return;
        const linea = state.pedido.lineas.find(l => l.producto_id === productoId);
        if (!linea) return;

        const action = await openAccionesLineaModal({ linea });
        if (!action) return;

        if (action === 'cambiar-lote') {
          const loteActual = getLoteMemorizado(productoId);
          const nuevo = await openCambiarLoteModal({ linea, loteActual });
          if (nuevo === undefined) return;
          changeLote(productoId, nuevo);
          await openAlert({
            tipo: 'ok',
            titulo: nuevo ? 'Lote actualizado' : 'Lote borrado',
            detalle: nuevo
              ? `Próximos escaneos de "${linea.descripcion}" usarán lote ${nuevo}.`
              : `Próximo escaneo de "${linea.descripcion}" preguntará el lote desde cero.`,
          });
          return;
        }

        if (action === 'cambiar-cantidad') {
          const nuevaCant = await openEditarLineaModal({ linea });
          if (nuevaCant === undefined) return;
          await aplicarPatchCantidad(linea, nuevaCant);
          return;
        }

        if (action === 'quitar') {
          const ok = await openAlert({
            tipo: 'warn',
            titulo: '¿Quitar línea del pedido?',
            detalle: `"${linea.descripcion}" se marcará como eliminada y desaparecerá de la lista.`,
            accion: 'Quitar',
            accion2: 'Cancelar',
          });
          if (ok !== 'primary') return;
          await aplicarDeleteLinea(linea);
          return;
        }
      }

      // POST /pedidos/{id}/lineas/{linea_id} cantidad — maneja conflicto 409.
      async function aplicarPatchCantidad(linea, cantidad) {
        try {
          await apiPatch(`/pedidos/${pedidoId}/lineas/${linea.linea_id}`, { cantidad });
          await refrescarPedido();
        } catch (e) {
          if (e?.status === 401) return;
          if (e?.status === 409 && e.detail && typeof e.detail === 'object' && e.detail.conflicto) {
            const c = e.detail.conflicto;
            await openAlert({
              tipo: 'warn',
              titulo: 'Cantidad menor a lo ya empaquetado',
              detalle: `${c.descripcion}: ya hay ${c.ya_empacado} unidades en ${c.cajas_afectadas.length} ${c.cajas_afectadas.length === 1 ? 'caja' : 'cajas'}. Edita o anula esas cajas desde la vista de Estructura antes de bajar la cantidad pedida.`,
              accion: 'Abrir estructura',
              accion2: 'Cerrar',
            }).then(r => {
              if (r === 'primary') root.querySelector('#btn-estructura').click();
            });
            return;
          }
          await openAlert({ tipo: 'error', titulo: 'No se pudo cambiar la cantidad', detalle: e?.message || 'Error' });
        }
      }

      async function aplicarDeleteLinea(linea) {
        try {
          await apiDel(`/pedidos/${pedidoId}/lineas/${linea.linea_id}`);
          await refrescarPedido();
        } catch (e) {
          if (e?.status === 401) return;
          if (e?.status === 409 && e.detail?.conflicto) {
            await openAlert({
              tipo: 'warn',
              titulo: 'No se puede quitar todavía',
              detalle: 'Hay unidades empaquetadas de este producto. Anula las cajas afectadas desde Estructura antes de quitar la línea.',
            });
            return;
          }
          await openAlert({ tipo: 'error', titulo: 'No se pudo quitar la línea', detalle: e?.message || 'Error' });
        }
      }

      async function refrescarPedido() {
        try {
          const pedido = await apiGet(`/pedidos/${pedidoId}`);
          updatePedido(pedido);
          rerender(root);
          // En paralelo: comprobar flags de sync por si pedido está cerrado.
          await checkSyncStatus();
          // Y refrescar plan de packing — los overrides cambian el pendiente.
          _refrescarPackingPlan(pedidoId);
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({ tipo: 'error', titulo: 'No se pudo refrescar el pedido', detalle: e?.message || 'Error' });
        }
      }

      // Fase 10 / 7.4 — banner sticky de sincronización post-cierre.
      // Llama a estructura para leer los flags pedido_cerrado / sync_pendiente
      // y enseña/oculta el banner. Lo invocamos al montar y tras cada mutación.
      // De paso: limpia paletActivo si el palet activo ya no existe en
      // estructura (típico tras eliminar palet vacío desde Estructura).
      async function checkSyncStatus() {
        const banner = root.querySelector('#sync-banner');
        if (!banner) return;
        try {
          const estr = await apiGet(`/pedidos/${pedidoId}/estructura`);

          const st = getState();
          if (st.paletActivo) {
            const sigueExistiendo = (estr.palets || []).some(p => p.sscc === st.paletActivo.sscc);
            if (!sigueExistiendo) {
              clearPaletActivo();
              rerender(root);
            }
          }

          if (estr.pedido_cerrado && estr.sync_pendiente) {
            const expId = estr.expedicion_id;
            banner.hidden = false;
            banner.innerHTML = `
              <span style="display:inline-flex;">${icon('alert-triangle', { size: 22 })}</span>
              <span style="flex:1;">
                <strong>Cambios sin sincronizar</strong>
                <span style="display:block;font-size:var(--fs-xs);opacity:0.9;">El envío DOL-${expId || '?'} aún no refleja las últimas ediciones.</span>
              </span>
              <button class="btn" id="btn-sync-now" style="
                background:#fff;color:var(--c-partial);border:0;
                min-height:40px;padding:0 0.85rem;
                font-size:var(--fs-sm);font-weight:700;
              ">Sincronizar</button>`;
            banner.querySelector('#btn-sync-now').addEventListener('click', () => triggerSync(false));
          } else {
            banner.hidden = true;
            banner.innerHTML = '';
          }
        } catch (e) {
          if (e?.status === 401) return;
          // No bloqueamos la UI por esto — el badge se queda como estaba.
          console.warn('checkSyncStatus falló', e);
        }
      }

      async function triggerSync(force) {
        try {
          const res = await apiPost(`/pedidos/${pedidoId}/resincronizar`, { force_recrear: !!force });
          if (res.accion_ejecutada === 'requiere_confirmacion') {
            const expId = res.expedicion_id;
            const ok = await openAlert({
              tipo: 'warn',
              titulo: '¿Anular y crear envío nuevo?',
              detalle: `Los cambios afectan a las líneas del envío. Esto anulará el envío DOL-${expId} en el sistema y creará uno nuevo. ¿Continuar?`,
              accion: 'Sí, anular y recrear',
              accion2: 'Cancelar',
            });
            if (ok !== 'primary') return;
            await triggerSync(true);
            return;
          }
          if (res.accion_ejecutada === 'pendiente_sync') {
            await openAlert({
              tipo: 'warn',
              titulo: 'Sistema no responde',
              detalle: 'Los cambios quedaron encolados. Se reintentarán automáticamente cuando vuelva el sistema.',
            });
          } else if (res.accion_ejecutada === 'actualizar_extrafields') {
            await openAlert({
              tipo: 'ok',
              titulo: 'Sincronizado',
              detalle: `Envío DOL-${res.expedicion_id} actualizado.`,
            });
          } else if (res.accion_ejecutada === 'anular_y_recrear') {
            await openAlert({
              tipo: 'ok',
              titulo: 'Envío recreado',
              detalle: `DOL-${res.expedicion_id_anterior} → DOL-${res.expedicion_id}.`,
            });
          } else if (res.accion_ejecutada === 'sin_cambios') {
            await openAlert({ tipo: 'ok', titulo: 'Sin cambios', detalle: 'Ya estaba sincronizado.' });
          }
          await checkSyncStatus();
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({ tipo: 'error', titulo: 'No se pudo sincronizar', detalle: e?.message || 'Error' });
        }
      }

      // Badge "Modificado" del topbar — abre el listado de cambios con
      // opción de deshacer cada uno. Solo es visible cuando hay overrides
      // activos (rerender se ocupa del hidden).
      root.querySelector('#badge-modificado').addEventListener('click', async () => {
        const st = getState();
        if (!st.pedido) return;
        const huboCambios = await openOverridesModal({ pedidoId: st.pedido.pedido_id });
        if (huboCambios) {
          // Tras deshacer overrides el pedido cambió; refrescamos líneas + badge.
          await refrescarPedido();
        }
      });

      // Botón "📚 Estructura" abre el modal con el árbol del pedido.
      // No requiere que la caja-en-curso esté en ningún estado concreto.
      root.querySelector('#btn-estructura').addEventListener('click', async () => {
        const st = getState();
        if (!st.pedido) return;
        await openEstructuraModal({
          pedidoId: st.pedido.pedido_id,
          onChange: () => {
            rerender(root);
            // Tras mutaciones en estructura (anular/editar/mover) el flag
            // de sync_pendiente puede cambiar si el pedido está cerrado.
            checkSyncStatus();
            // También recalculamos el plan de packing — cajas anuladas/movidas
            // cambian lo pendiente.
            _refrescarPackingPlan(st.pedido.pedido_id);
          },
        });
      });

      // Botón banner del plan de packing → abre modal con detalle.
      root.querySelector('#packing-banner').addEventListener('click', async () => {
        const st = getState();
        if (!st.packingPlan || !st.pedido) return;
        await openPlanPackingModal({
          plan: st.packingPlan,
          pedidoId: st.pedido.pedido_id,
          onPlanChange: (nuevo) => setPackingPlan(nuevo),
        });
      });

      // Cargar pedido + plan packing en paralelo.
      try {
        const pedido = await apiGet(`/pedidos/${pedidoId}`);
        loadPedido(pedido);
        root.querySelector('#ped-title').firstChild.textContent = pedido.referencia + ' ';
        root.querySelector('#ped-cliente').textContent = pedido.cliente_nombre || '— sin cliente —';
        rerender(root);
        // Plan packing en background — no bloqueamos la vista si tarda.
        _refrescarPackingPlan(pedidoId);
      } catch (e) {
        if (e?.status === 401) return;
        root.querySelector('#lineas').innerHTML = `
          <div class="alert --error" style="margin-top:1rem;">
            <div class="ico">${icon('octagon-x', { size: 24 })}</div>
            <div class="body">
              <strong>No se pudo cargar el pedido</strong>
              <span>${escapeHtml(e?.message || 'Error desconocido')}</span>
            </div>
          </div>`;
        return;
      }

      unsubscribe = subscribe(() => rerender(root));

      // Botón Escanear — reutiliza el mismo helper que la re-apertura
      // automática tras el modal de lote.
      root.querySelector('#btn-scan').addEventListener('click', _startScannerSession);

      // Botón "+ Añadir producto al pedido" — crea override 'añadida'.
      root.querySelector('#btn-anadir-producto').addEventListener('click', async () => {
        const datos = await openAnadirProductoModal();
        if (!datos) return;
        try {
          const res = await apiPost(`/pedidos/${pedidoId}/lineas`, datos);
          await refrescarPedido();
          if (res?.pendiente_id) {
            // Sub-fase C: producto nuevo enviado a validación admin
            await openAlert({
              tipo: 'warn',
              titulo: 'Enviado a validación',
              detalle: `"${datos.descripcion}" queda pendiente hasta que un admin lo valide. Aparece en el listado marcado como [pendiente].`,
            });
          } else {
            await openAlert({
              tipo: 'ok',
              titulo: 'Producto añadido',
              detalle: `"${datos.descripcion}" se ha añadido al pedido. Ya lo puedes escanear o empacar.`,
            });
          }
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({ tipo: 'error', titulo: 'No se pudo añadir', detalle: e?.message || 'Error' });
        }
      });

      // Botón "Nuevo palet" (Fase 10)
      root.querySelector('#btn-nuevo-palet').addEventListener('click', async () => {
        const state = getState();
        if (!state.pedido) return;
        const btn = root.querySelector('#btn-nuevo-palet');
        btn.disabled = true;
        const original = btn.textContent;
        btn.textContent = '⏳ Creando palet…';
        try {
          const res = await apiPost('/palets/crear', {
            pedido_id: state.pedido.pedido_id,
            pedido_referencia: state.pedido.referencia,
            cliente_nombre: state.pedido.cliente_nombre,
          });
          setPaletActivo(res);  // {sscc, numero_palet, ...}
          if (res.modo === 'pdf') {
            await openPaletPdfModal({ numero: res.numero_palet, sscc: res.sscc, path: res.etiqueta_url });
          } else {
            await openPaletOkModal({ numero: res.numero_palet, sscc: res.sscc });
          }
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({
            tipo: 'error',
            titulo: 'No se pudo crear el palet',
            detalle: e?.message || 'Error desconocido',
          });
        } finally {
          btn.textContent = original;
          btn.disabled = false;
        }
      });

      // Botón Caja lista. Abre PRIMERO un sheet con el contenido a imprimir
      // (sustituye a la sección "En la caja actual" que vivía en scroll).
      // Si el operario confirma → POST /cajas/crear → modal SSCC/PDF.
      root.querySelector('#btn-caja').addEventListener('click', async () => {
        const state = getState();
        if (!state.pedido || state.cajaActual.lineas.length === 0) return;
        const numeroCaja = state.ssccs.length + 1;
        const peso = pesoCajaActual();

        // 1) Preview pre-impresión
        const ok = await openConfirmarCajaModal({
          numeroCaja,
          lineas: state.cajaActual.lineas,
          pesoKg: peso,
          paletActivo: state.paletActivo,
        });
        if (!ok) return;

        // 2) Impresión real
        const payload = {
          pedido_id: state.pedido.pedido_id,
          pedido_referencia: state.pedido.referencia,
          cliente_nombre: state.pedido.cliente_nombre,
          numero_caja: numeroCaja,
          peso_total_kg: peso,  // el backend lo recalcula si null
          lineas: state.cajaActual.lineas,
          // Si hay palet activo, lo enviamos para que el backend vincule la caja.
          palet_sscc: state.paletActivo?.sscc || null,
        };
        const btn = root.querySelector('#btn-caja');
        btn.disabled = true;
        btn.textContent = '⏳ Imprimiendo…';
        try {
          const res = await apiPost('/cajas/crear', payload);
          const paletAntes = state.paletActivo;  // snapshot antes de closeCaja
          closeCaja(res.sscc, paletAntes?.sscc || null);  // registra asociación palet
          const paletInfo = paletAntes
            ? { numero: paletAntes.numero, totalCajasAhora: cajasEnPalet(paletAntes.sscc) }
            : null;
          if (res.modo === 'pdf') {
            await openCajaPdfModal({ numero: numeroCaja, sscc: res.sscc, path: res.etiqueta_url, palet: paletInfo });
          } else {
            await openCajaOkModal({ numero: numeroCaja, sscc: res.sscc, palet: paletInfo });
          }
          // El backend invalidó el cache al guardar la caja — pedimos el plan nuevo.
          _refrescarPackingPlan(pedidoId);
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({
            tipo: 'error',
            titulo: 'No se pudo cerrar la caja',
            detalle: e?.message || 'Error desconocido',
          });
        } finally {
          btn.innerHTML = `${icon('package', { size: 26 })} Caja lista`;
          rerender(root);
        }
      });

      // Botón Cerrar pedido
      root.querySelector('#btn-cerrar').addEventListener('click', async () => {
        const state = getState();
        if (!state.pedido || state.ssccs.length === 0) return;

        let permitirIncompleto = false;
        let parcial = false;
        if (!pedidoCompleto()) {
          const eleccion = await openIncompletoModal();
          if (eleccion === 'seguir') return;
          if (eleccion === 'parcial') {
            parcial = true;
            permitirIncompleto = true;
          } else if (eleccion === 'definitivo') {
            parcial = false;
            permitirIncompleto = true;
          }
        }

        const btn = root.querySelector('#btn-cerrar');
        btn.disabled = true;
        const original = btn.textContent;
        btn.textContent = '⏳ Cerrando…';
        try {
          const peso = state.cajaActual.lineas.length === 0
            ? null
            : pesoCajaActual();
          const res = await apiPost(`/pedidos/${state.pedido.pedido_id}/cerrar`, {
            ssccs: state.ssccs,
            permitir_incompleto: permitirIncompleto,
            parcial,
          });

          if (res.es_parcial) {
            // Envío parcial: el operario sigue con el pedido. No reseteamos
            // todo el estado — solo limpiamos lo que se acaba de enviar
            // (ssccs/paletActivo/cajasPorPalet) preservando cajaActual y
            // lotes en curso. Al recargar, `cantidad_enviada` del backend
            // mantiene el progreso visible (3/12, 6/12...).
            await openAlert({
              tipo: 'ok',
              titulo: `Envío parcial #${res.numero_envio} enviado`,
              detalle: `${state.ssccs.length} ${state.ssccs.length === 1 ? 'caja' : 'cajas'} fueron al envío DOL-${res.expedicion_id ?? '?'}. El pedido sigue abierto para nuevos envíos.`,
            });
            // Limpiar solo lo que se envió: ssccs, palet activo y mapping
            // cajasPorPalet. cajaActual y lotes se mantienen (si los hay).
            const pedidoId = state.pedido.pedido_id;
            // Persistir snapshot mínimo antes de recargar.
            const cajaActualBackup = state.cajaActual;
            const lotesBackup = state.lotes;
            resetState();
            // Recargar pedido fresco — empaquetado se inicia con cantidad_enviada.
            const pedidoFresco = await apiGet(`/pedidos/${pedidoId}`);
            loadPedido(pedidoFresco);
            // Restaurar caja-en-curso (si la había).
            if (cajaActualBackup?.lineas?.length) {
              const st = getState();
              st.cajaActual.lineas = cajaActualBackup.lineas;
              st.lotes = lotesBackup;
              for (const l of cajaActualBackup.lineas) {
                st.empaquetado[l.producto_id] = (st.empaquetado[l.producto_id] || 0) + l.cantidad;
              }
            }
            rerender(root);
            return;
          }

          // Cierre definitivo: lightbox + volver a lista.
          await openPedidoCerradoLightbox({
            pedidoRef: state.pedido.referencia,
            cajas: state.ssccs.length,
            peso: peso,
          });
          resetState();
          navigate('#/pedidos', { replace: true });
        } catch (e) {
          if (e?.status === 401) return;
          await openAlert({
            tipo: 'error',
            titulo: 'No se pudo cerrar el pedido',
            detalle: e?.message || 'Error desconocido',
          });
          btn.disabled = false;
          btn.textContent = original;
        }
      });

      _requestWakeLock();
      // Tras montar, comprobar si el pedido ya estaba cerrado con cambios
      // post-cierre pendientes (caso recargar la pestaña tras editar).
      checkSyncStatus();
    },

    unmount() {
      mounted = false;
      try { scannerCloser?.(); } catch {}
      scannerCloser = null;
      try { unsubscribe?.(); } catch {}
      unsubscribe = null;
      _releaseWakeLock();
    },
  };
}
